import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import showToast from '../utils/toastHelper';
import '../assets/styles/auth.css';

const RegisterOrganization = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    phoneNumber: '',
    adminName: '',
    adminEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [setupEmailSent, setSetupEmailSent] = useState(false);

  const resendSetup = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/organizations/resend-setup`, { email: formData.adminEmail });
      setSetupEmailSent(true);
      showToast('Check your email to complete setup.', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Could not send setup email. Please try again later.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-generate slug from organization name
    if (name === 'name') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData({ ...formData, name: value, slug });
    } else if (name === 'slug') {
      // Allow manual slug editing
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-');
      setFormData({ ...formData, slug });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/organizations/register`,
        formData
      );
      setSuccess(true);
      setSetupEmailSent(response.data.data.setupEmailSent);
      setIsSubmitting(false);
      showToast(
        response.data.data.setupEmailSent ? 'Check your email to complete setup.' : 'Please request a new setup email.',
        response.data.data.setupEmailSent ? 'success' : 'warning'
      );
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to submit registration. Please try again.';
      showToast(errorMessage, 'error');
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="org-setup-layout">
        <main className="org-setup-page">
          <section className="org-setup-card" aria-labelledby="org-setup-title">
            <div className="org-setup-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
            </div>
            <h1 id="org-setup-title">Check your email</h1>
            <p className="org-setup-message">
              {setupEmailSent
                ? 'Follow the link in your email to complete your account setup.'
                : 'Request a setup email below to complete your account setup.'}
            </p>
            <p className="org-setup-email">{formData.adminEmail}</p>
            {!setupEmailSent && <Alert variant="warning">
              We couldn't send your setup email. Please try again.
            </Alert>}
            <div className="org-setup-footer">
              <span>Didn't receive it?</span>
              <button type="button" onClick={resendSetup} className="org-setup-resend" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Resend setup email'}
              </button>
            </div>
          </section>
        </main>
        <footer className="org-setup-site-footer">
          <span>© {new Date().getFullYear()} VoteWave. All rights reserved.</span>
          <div className="org-setup-legal-links">
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="auth-card">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h2>Register Your Organization</h2>
                  <p className="text-muted">
                    Join VoteWave to create and manage secure online elections for your organization
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="btn btn-link btn-sm"
                    type="button"
                  >
                    ← Back to Login
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Organization Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="e.g., Student Council Association"
                        />
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="slug" className="form-label">
                          Organization Slug <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="slug"
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          required
                          placeholder="e.g., student-council"
                        />
                        <small className="text-muted">
                          Used in your organization's URL (lowercase, hyphens only)
                        </small>
                      </div>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      required
                      placeholder="Brief description of your organization"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="phoneNumber" className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="+256 700 000 000"
                    />
                    <small className="text-muted">
                      Contact phone number for your organization
                    </small>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3">Administrator Account</h5>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="adminName" className="form-label">
                          Admin Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="adminName"
                          name="adminName"
                          value={formData.adminName}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                        />
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="adminEmail" className="form-label">
                          Admin Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="adminEmail"
                          name="adminEmail"
                          value={formData.adminEmail}
                          onChange={handleChange}
                          required
                          placeholder="admin@example.com"
                        />
                        <small className="text-muted">
                          This email will receive your account setup link immediately
                        </small>
                      </div>
                    </Col>
                  </Row>

                  <Alert variant="info" className="mt-3">
                    After registering, check your email to complete your account setup.
                  </Alert>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 mt-3"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Registration'
                    )}
                  </button>
                </form>

                <div className="text-center mt-3">
                  <button
                    onClick={() => navigate('/login')}
                    className="btn btn-link"
                  >
                    Already have an account? Login
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RegisterOrganization;
